import { createStore } from "vuex";
import proctoring from "./useProctoringStore";
import axios from "axios";
import createPersistedState from "vuex-persistedstate";
import SecureLS from "secure-ls";

const ls = new SecureLS({ isCompression: false });

export default createStore({
  modules: {
    proctoring,
  },

  state: {
    access: null,
    refresh: null,
    user: null,
    roles: [],
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    tokenExp: null,
    josh_id: null,
    loginError: null,
    is_face: false,
    is_nri: false,
  },

  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    user: (state) => state.user,
    access: (state) => state.access,
    refresh: (state) => state.refresh,
    roles: (state) => state.roles,
    getAccessToken: (state) => state.accessToken,
    getRefreshToken: (state) => state.refreshToken,
    getTokenExp: (state) => state.tokenExp,
    josh_id: (state) => state.josh_id,
    login_error: (state) => state.loginError,
    is_face: (state) => state.is_face,
    is_nri: (state) => state.is_nri,
  },

  mutations: {
    setLoggedInInfo(state, payload) {
      const userData = payload.data;

      state.isAuthenticated = true;

      state.access = userData.access;
      state.refresh = userData.refresh;

      state.user = {
        id: userData.user_id,
        full_name: userData.full_name,
        email: userData.email,
        phone: userData.phone,
      };

      state.roles = userData.roles || [];
      state.is_face = userData.is_face || false;
      state.is_nri = userData.is_nri || false;
    },

    setLogout(state) {
      state.access = null;
      state.refresh = null;
      state.user = null;
      state.roles = [];
      state.isAuthenticated = false;
      state.accessToken = null;
      state.refreshToken = null;
      state.josh_id = null;
      state.loginError = null;
      state.is_face = false;
      state.is_nri = false;

      localStorage.removeItem("exam_id");
      localStorage.removeItem("exam_name");
      localStorage.removeItem("room_id");
      localStorage.removeItem("room_name");
      localStorage.removeItem("exam_attempt_id");
    },
    setAccessToken(state, token) {
      state.accessToken = token;
    },
    setRefreshToken(state, token) {
      state.refreshToken = token;
    },
    setExpDate(state, date) {
      state.tokenExp = date;
    },
    setJoshInfo(state, data) {
      state.josh_id = data;
    },
    setLoginError(state, data) {
      state.loginError = data;
    },
    setIsFace(state, isFace) {
      state.is_face = isFace;
    },
    setIsNri(state, isNri) {
      state.is_nri = isNri;
    },
  },

  actions: {
    async loginWithPassword({ commit, dispatch }, data) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/login/`,
          data
        );

        commit("setLoggedInInfo", response.data);
        commit("setAccessToken", response.data.data.access);
        commit("setRefreshToken", response.data.data.refresh);
        if (response.data.data.josh_id != null) {
          commit("setJoshInfo", response.data.data.josh_id);
        }

        dispatch("saveToken");

        return response.data;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error?.detail ||
          "Login failed";

        commit("setLoginError", message);

        return {
          status: error.response?.status,
          message,
        };
      }
    },
    async sendStudentOtp(_, { email }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/login/send-otp/`,
          { email }
        );
        return response.data;
      } catch (error) {
        console.error("Send OTP Error:", error);
        return error.response?.data ?? null;
      }
    },

    async loginWithOtp({ commit, dispatch }, { email, otp }) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/login/verify-otp/`,
          { email, otp }
        );
        commit("setLoggedInInfo", response.data);
        commit("setAccessToken", response.data.data.access);
        commit("setRefreshToken", response.data.data.refresh);
        if (response.data.data.josh_id != null) {
          commit("setJoshInfo", response.data.data.josh_id);
        }
        dispatch("saveToken");
        return response.data;
      } catch (error) {
        console.error("OTP Login Error:", error);
        return error.response?.data ?? null;
      }
    },

    logout({ commit }) {
      commit("setLogout");
      commit("setExpDate", null);
    },
    saveToken({ state, commit }) {
      const token = state.accessToken;
      if (!token) return;

      try {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        );
        const decoded = JSON.parse(jsonPayload);

        const expDate = new Date(decoded.exp * 1000);
        commit("setExpDate", expDate);
      } catch (err) {
        console.error("Failed to decode JWT:", err);
      }
    },
    async refreshAccessToken({ state, commit, dispatch }) {
      try {
        const refresh = state.refresh || state.refreshToken;
        if (!refresh) return null;

        const response = await axios.post(
          `${process.env.VUE_APP_API_ROOT}/auth/refresh/`,
          { refresh }
        );

        if (response.data && response.data.access) {
          commit("setAccessToken", response.data.access);
          state.access = response.data.access;
          dispatch("saveToken");
          return response.data.access;
        }
      } catch (error) {
        console.error("Token refresh failed:", error);
        return null;
      }
    },
  },

  plugins: [
    createPersistedState({
      key: "exam-app",
      storage: {
        getItem: (key) => {
          try {
            return ls.get(key);
          } catch (error) {
            console.warn("Cleared old unencrypted state:", error);
            window.localStorage.removeItem(key);
            return null;
          }
        },
        setItem: (key, value) => ls.set(key, value),
        removeItem: (key) => ls.remove(key),
      },
      paths: [
        "access",
        "refresh",
        "user",
        "roles",
        "isAuthenticated",
        "accessToken",
        "refreshToken",
        "tokenExp",
        "is_face",
        "is_nri",
      ],
    }),
  ],
});
